import { useMemo } from 'react';

import { useResponsive } from '@sg/garnish';
import {
  type CustomizationMachineActorRef,
  type CustomizationMachineTypes,
  useCustomizationContext,
  useCustomizationHelpers,
} from '@sg/shared-fe-features-customization';

import { useIsFoldableDevice } from '../../../../../../../../hooks/useIsFoldableDevice';
import { buildCustomizationIngredientRows } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Flattens the modification groups into the virtualized list's rows, sorting
 * each section's ingredients the way the eager grid used to.
 */
export const useCustomizationIngredientRows = (params: Params) => {
  const { actor, modificationGroups } = params;

  const { currentBreakpoint, match } = useResponsive();
  const isFoldableDevice = useIsFoldableDevice();
  const { sortModifications } = useCustomizationHelpers({ actor });
  const { initialModificationsMap } = useCustomizationContext({ actor });

  // ─── Derived ─────────────────────────────────────────────────────────

  // NOTE: A foldable's open display, such as the iPhone Duo's, sits in the SM
  //       breakpoint but is wide enough for a third column.
  const isOpenFoldableDisplay = currentBreakpoint.isSM && isFoldableDevice;
  const itemsPerRow = isOpenFoldableDisplay ? 3 : match(INGREDIENTS_PER_ROW);

  const rows = useMemo(() => {
    if (!modificationGroups || !initialModificationsMap) return [];

    const sections = modificationGroups.map((group) => ({
      group,
      modifications: sortModifications(initialModificationsMap, group),
    }));

    return buildCustomizationIngredientRows({ sections, itemsPerRow });
  }, [modificationGroups, initialModificationsMap, sortModifications, itemsPerRow]);

  // ─────────────────────────────────────────────────────────────────────

  return { rows, itemsPerRow };
};

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Ingredients per row, per breakpoint.
 */
const INGREDIENTS_PER_ROW = [3, 2, 4, 5];

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  actor: CustomizationMachineActorRef;
  modificationGroups: CustomizationMachineTypes.ExtendedModifierGroup[] | undefined;
};
